import axios from 'axios';
 
// Relative in every environment, so requests go to whatever origin served the
// page. Development used to hardcode http://localhost:9502, which bypassed the
// CRA proxy that frontend/package.json already declares and made dev the only
// cross-origin setup in the project. Three things broke because of it:
// opening the "On Your Network" URL on a phone asked THAT phone for
// localhost:9502 and failed outright (so a mobile-first site could not be
// tested on mobile); dev depended on the backend CORS allow-list staying in
// sync with whatever port CRA picked; and dev exercised a request path
// production never uses. REACT_APP_API_URL still overrides for a split deploy.
const getBaseUrl = () =>
  process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '';

const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Pick the credential that matches the endpoint. Preferring the user token
    // unconditionally meant that an owner who was also signed in as a normal
    // user in the same browser sent the wrong token to every admin endpoint,
    // got a 401, and was bounced to /admin/login on every request.
    const url = config.url || '';
    const isAdminEndpoint = /^\/?(api\/)?(admin|admin-management|analytics|ads)(\/|$)/.test(
      url.replace(/^https?:\/\/[^/]+/, '')
    );

    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('token');
    const token = isAdminEndpoint ? (adminToken || userToken) : (userToken || adminToken);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors.
    // Only clear the credential that actually failed - wiping both logged the
    // user out of the public site whenever an admin call 401'd, and vice versa.
    if (error.response?.status === 401) {
      const url = (error.config?.url || '').replace(/^https?:\/\/[^/]+/, '');
      const wasAdmin = /^\/?(api\/)?(admin|admin-management|analytics|ads)(\/|$)/.test(url);
      const target = wasAdmin ? '/admin/login' : '/login';

      localStorage.removeItem(wasAdmin ? 'adminToken' : 'token');

      // Do not bounce if we are already on the login page - that produces a
      // redirect loop when the login request itself returns 401.
      if (!window.location.pathname.startsWith(target)) {
        window.location.href = target;
      }
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

export default api;
